import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useToast } from '../../components/Toast';
import { useApi } from '../../services/api';
import { formatDate } from '../../utils/dateFormatter';

export default function Settings() {
    const { user } = useAuth();
    const { tenant, refreshTenant } = useTenant();
    const { showToast } = useToast();
    const api = useApi();

    const [profile, setProfile] = useState({
        name: user?.name || '',
        email: user?.email || '',
        position: user?.position || '',
    });

    // Domain settings state
    const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
    const [newDomain, setNewDomain] = useState('');
    const [requireApproval, setRequireApproval] = useState(false);
    const [savingDomains, setSavingDomains] = useState(false);

    useEffect(() => {
        if (tenant) {
            setAllowedDomains(tenant.allowedDomains || []);
            setRequireApproval(tenant.requireApproval || false);
        }
    }, [tenant]);

    function handleSave(e: React.FormEvent) {
        e.preventDefault();
        api.updateProfile({ name: profile.name, position: profile.position })
            .then(() => {
                showToast('Settings saved successfully!', 'success');
            })
            .catch((error) => {
                console.error('Failed to save settings:', error);
                showToast('Failed to save settings', 'error');
            });
    }

    function addDomain() {
        const domain = newDomain.trim().toLowerCase();
        if (domain && !allowedDomains.includes(domain)) {
            setAllowedDomains([...allowedDomains, domain]);
            setNewDomain('');
        }
    }

    function removeDomain(domain: string) {
        setAllowedDomains(allowedDomains.filter(d => d !== domain));
    }

    async function saveDomainSettings() {
        setSavingDomains(true);
        try {
            await api.updateDomainSettings({ allowedDomains, requireApproval });
            showToast('Domain settings saved successfully!', 'success');
            if (refreshTenant) refreshTenant();
        } catch (error: any) {
            showToast(error.message || 'Failed to save domain settings', 'error');
        } finally {
            setSavingDomains(false);
        }
    }

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6 text-text-primary">Settings</h1>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Profile Settings */}
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <h2 className="text-xl font-semibold mb-4 text-text-primary">Profile Settings</h2>
                    <form onSubmit={handleSave}>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Name
                            </label>
                            <input
                                type="text"
                                value={profile.name}
                                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                value={profile.email}
                                disabled
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background/50 text-text-muted"
                            />
                            <p className="text-xs text-text-muted mt-1">Email cannot be changed</p>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Position
                            </label>
                            <input
                                type="text"
                                value={profile.position}
                                onChange={(e) => setProfile({ ...profile, position: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90"
                        >
                            Save Changes
                        </button>
                    </form>
                </div>

                {/* Organization Info */}
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <h2 className="text-xl font-semibold mb-4 text-text-primary">Organization</h2>
                    <div className="space-y-3">
                        <div>
                            <p className="text-sm text-text-muted">Organization Name</p>
                            <p className="font-medium text-text-primary">{tenant?.name}</p>
                        </div>
                        <div>
                            <p className="text-sm text-text-muted">Slug</p>
                            <p className="font-medium text-text-primary">{tenant?.slug}</p>
                        </div>
                        <div>
                            <p className="text-sm text-text-muted">Your Role</p>
                            <p className="font-medium capitalize">{user?.role?.replace('_', ' ')}</p>
                        </div>
                    </div>
                </div>

                {/* Domain Settings */}
                <div className="bg-surface rounded-lg shadow p-6 border border-border lg:col-span-2">
                    <h2 className="text-xl font-semibold mb-4 text-text-primary">Domain & Signup Settings</h2>
                    <p className="text-sm text-text-secondary mb-4">
                        Control who can sign up for your organization. Add email domains to allow, and choose whether new signups need admin approval.
                    </p>

                    <div className="space-y-6">
                        {/* Allowed Domains */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Allowed Email Domains
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                                    placeholder="e.g., company.com"
                                    className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                                />
                                <button
                                    type="button"
                                    onClick={addDomain}
                                    className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90"
                                >
                                    Add
                                </button>
                            </div>
                            {allowedDomains.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {allowedDomains.map(domain => (
                                        <span
                                            key={domain}
                                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                                        >
                                            @{domain}
                                            <button
                                                onClick={() => removeDomain(domain)}
                                                className="hover:text-red-500"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-text-muted">
                                    No domains configured. Anyone with a valid email can sign up.
                                </p>
                            )}
                        </div>

                        {/* Require Approval */}
                        <div className="flex items-center justify-between p-4 bg-background rounded-lg">
                            <div>
                                <p className="font-medium text-text-primary">Require Admin Approval</p>
                                <p className="text-sm text-text-muted">
                                    New signups will need admin approval before they can access the app.
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={requireApproval}
                                    onChange={(e) => setRequireApproval(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                            </label>
                        </div>

                        <button
                            type="button"
                            onClick={saveDomainSettings}
                            disabled={savingDomains}
                            className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50"
                        >
                            {savingDomains ? 'Saving...' : 'Save Domain Settings'}
                        </button>
                    </div>
                </div>

                {/* Preferences */}
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <h2 className="text-xl font-semibold mb-4 text-text-primary">Preferences</h2>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium text-text-primary">Email Notifications</p>
                                <p className="text-sm text-text-muted">Receive updates about claims and games</p>
                            </div>
                            <input type="checkbox" className="h-5 w-5" defaultChecked />
                        </div>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium text-text-primary">Activity Notifications</p>
                                <p className="text-sm text-text-muted">Get notified about team activity</p>
                            </div>
                            <input type="checkbox" className="h-5 w-5" defaultChecked />
                        </div>
                    </div>
                </div>

                {/* Account */}
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <h2 className="text-xl font-semibold mb-4 text-text-primary">Account</h2>
                    <div className="space-y-3">
                        <div>
                            <p className="text-sm text-text-muted">Account Status</p>
                            <p className="font-medium text-green-600">Active</p>
                        </div>
                        <div>
                            <p className="text-sm text-text-muted">Member Since</p>
                            <p className="font-medium text-text-primary">
                                {user?.createdAt ? formatDate(user.createdAt) : 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

